import React, { useState } from 'react';
import { FileText, Download, Share2, X } from 'lucide-react';
import { generateSalePDF, sharePDF, downloadPDF, shareToWhatsApp } from '../../../../services/pdfGenerator';

const ActionButtons = ({ sale }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [error, setError] = useState(null);

  // Función para abrir el bottom sheet con opciones
  const handleOpenOptions = () => {
    if (!sale?.items?.length) {
      setError('Esta venta no tiene productos');
      return;
    }
    setShowOptions(true);
    setError(null);
  };

  // Función para generar el PDF
  const generatePDF = async () => {
    if (!sale?.items?.length) {
      setError('Esta venta no tiene productos');
      return null;
    }

    try {
      setIsGenerating(true);
      setError(null);
      const pdfData = await generateSalePDF(sale);
      setIsGenerating(false);
      return pdfData;
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      setError('Error al generar el PDF. Por favor, intente nuevamente.');
      setIsGenerating(false);
      return null;
    }
  };

  // Función para descargar el PDF
  const handleDownloadPDF = async () => {
    const pdfData = await generatePDF();
    if (!pdfData) return;
    
    downloadPDF(pdfData.blob, pdfData.filename);
    setShowOptions(false);
  };

  // Función para compartir por WhatsApp
  const handleShareWhatsApp = async () => {
    const pdfData = await generatePDF();
    if (!pdfData) return;
    
    try {
      setError(null);
      downloadPDF(pdfData.blob, pdfData.filename);
      shareToWhatsApp(
        `Comprobante de venta #${sale?.display_id || sale?.id}`,
        'Adjunto el comprobante de venta en PDF.'
      );
      setShowOptions(false);
    } catch (error) {
      console.error('Error al compartir por WhatsApp:', error);
      setError('Error al compartir por WhatsApp. Por favor, intente nuevamente.');
    }
  };

  // Función para compartir usando Web Share API
  const handleShareGeneric = async () => {
    const pdfData = await generatePDF();
    if (!pdfData) return;
    
    try {
      setError(null);
      await sharePDF({
        ...pdfData,
        title: `Venta ${sale?.display_id || sale?.id}`,
        text: 'Comprobante de venta'
      });
      setShowOptions(false);
    } catch (error) {
      console.error('Error al compartir:', error);
      setError('Error al compartir. Por favor, intente nuevamente.');
    }
  };

  // Si se están mostrando las opciones, mostrar el bottom sheet
  if (showOptions) {
    return (
      <>
        {/* Overlay para cerrar al tocar afuera */}
        <div 
          className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-40"
          onClick={() => setShowOptions(false)}
        />
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 p-4 z-50">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-gray-900 dark:text-white">Compartí tu comprobante en PDF con un click</h3>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowOptions(false);
              }}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {error && (
            <div className="mb-3 p-2 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={isGenerating}
              className="flex flex-col items-center justify-center p-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors gap-2"
            >
              <Download className="w-6 h-6" />
              <span className="text-sm font-medium">Descargar</span>
            </button>
            
            <button
              onClick={handleShareGeneric}
              disabled={isGenerating}
              className="flex flex-col items-center justify-center p-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors gap-2"
            >
              <Share2 className="w-6 h-6" />
              <span className="text-sm font-medium">Compartir</span>
            </button>
          </div>

          {isGenerating && (
            <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Botón principal para abrir opciones
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 p-4">
      <button
        onClick={handleOpenOptions}
        disabled={!sale?.items?.length}
        className="w-full py-3.5 bg-[#2C6859] dark:bg-[#245347] text-white rounded-lg hover:bg-[#245347] dark:hover:bg-[#1A3D34] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Share2 className="w-5 h-5" />
        {sale?.items?.length ? 'Compartir' : 'No hay productos para compartir'}
      </button>
      
      {error && (
        <div className="absolute top-0 left-0 right-0 transform -translate-y-full p-2 bg-red-100 text-red-700 text-sm text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default ActionButtons; 